/* Assortiment (Products) management page with variant support and image management
   Updated: Removed inline price editing from expanded variant rows — all 4 price tiers now shown as read-only badges;
   removed star/featured toggle button from product list (featured still settable in edit dialog);
   inline stock editing retained; fixed variant save using upsert; batch-loaded variants/images/FAQs */



import { useState, useEffect, useRef } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import SimpleMarkdownToolbar from '@/components/admin/SimpleMarkdownToolbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil as Edit2, Trash2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Package, X, Layers, Upload, Image as ImageIcon, MoveUp, MoveDown, CircleHelp as HelpCircle, ChevronRight, ChevronDown, Search, ArrowUpDown, Calculator, CalendarClock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { marked } from 'marked';
import VariantPricingCalculator from '@/components/admin/VariantPricingCalculator';

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
}

interface ProductSubcategory {
  id: string;
  name: string;
  slug: string;
  category_id: string;
  display_order: number;
  active: boolean;
}

interface ProductImage {
  id?: string;
  storage_path: string;
  display_order: number;
  is_primary: boolean;
  alt_text: string;
  variant_id?: string | null;
  url?: string;
}

interface ProductFAQ {
  id?: string;
  question: string;
  answer: string;
  display_order: number;
  active: boolean;
  isEditing?: boolean;
}

interface ProductVariant {
  id?: string;
  size: string;
  type: 'Kluit' | 'Pot' | 'Kale wortel';
  price_tier_1: number;
  price_tier_2?: number | null;
  price_tier_3?: number | null;
  price_tier_4?: number | null;
  minimum_order_quantity: number;
  stock_quantity: number;
  sku: string;
  slug: string;
  active: boolean;
  planten_per_meter: number;
  supplier: 'Kwekerij' | 'Overig';
  transport_method: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none';
  purchase_price_cents?: number | null;
  plants_per_box?: number | null;
  boxes_per_pallet_position?: number | null;
  box_cost_cents?: number | null;
  price_rounding_increment_cents?: number | null;
  isEditing?: boolean;
  images?: ProductImage[];
  price?: number;
  available_from?: string | null;
}

interface Product {
  id: string;
  name: string;
  scientific_name: string;
  description: string;
  image_url: string | null;
  category_id: string | null;
  base_slug: string | null;
  geslacht: string | null;
  sort: string | null;
  subcategory_id: string | null;
  standplaats: string[] | null;
  groei_per_jaar: string;
  snoeien: string;
  wintergroen: boolean;
  winterhard: boolean;
  maximale_hoogte: string;
  btw_percentage: number;
  featured: boolean;
  active: boolean;
  is_simple_product: boolean;
  simple_price: number;
  simple_sku: string;
  supplier: 'Kwekerij' | 'Overig';
  transport_method: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none';
  created_at: string;
  updated_at: string;
  product_categories?: ProductCategory;
  variants?: ProductVariant[];
  images?: ProductImage[];
  faqs?: ProductFAQ[];
  available_from?: string | null;
}

export default function AssortimentPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [subcategories, setSubcategories] = useState<ProductSubcategory[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingVariant, setEditingVariant] = useState<{ productId: string; variantIndex: number; field: 'stock' } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showAddVariantForm, setShowAddVariantForm] = useState(false);

  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    scientific_name: '',
    description: '',
    category_id: '',
    geslacht: '',
    sort: '',
    subcategory_id: '' as string,
    standplaats: [] as string[],
    groei_per_jaar: '',
    snoeien: '',
    wintergroen: false,
    winterhard: false,
    maximale_hoogte: '',
    btw_percentage: 9,
    featured: false,
    active: true,
    is_simple_product: false,
    simple_price: 0,
    simple_sku: '',
    supplier: 'Kwekerij' as 'Kwekerij' | 'Overig',
    transport_method: 'kuubkist' as 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none',
    available_from: null
  });

  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [productFAQs, setProductFAQs] = useState<ProductFAQ[]>([]);
  const [newVariant, setNewVariant] = useState<Partial<ProductVariant>>({
    size: '',
    type: 'Kluit',
    price_tier_1: 0,
    price_tier_2: null,
    price_tier_3: null,
    price_tier_4: null,
    minimum_order_quantity: 25,
    stock_quantity: 0,
    sku: '',
    planten_per_meter: 0,
    price_rounding_increment_cents: 5,
    supplier: 'Kwekerij',
    transport_method: 'kuubkist',
    active: true,
    available_from: null
  });
  const [newFAQ, setNewFAQ] = useState<Partial<ProductFAQ>>({
    question: '',
    answer: '',
    active: true
  });

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id]);

  useEffect(() => {
    let filtered = products;

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category_id === categoryFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.scientific_name?.toLowerCase().includes(query) ||
        product.product_categories?.name.toLowerCase().includes(query)
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    setFilteredProducts(sorted);
  }, [categoryFilter, searchQuery, sortOrder, products]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId?: string) => {
    try {
      let query = supabase
        .from('product_subcategories')
        .select('*')
        .order('display_order');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_categories (
            id,
            name,
            slug
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const products = (data || []) as any[];
      if (products.length === 0) {
        setProducts([]);
        setFilteredProducts([]);
        return;
      }

      const productIds = products.map(p => p.id);

      const [variantsRes, imagesRes, faqsRes] = await Promise.all([
        supabase
          .from('product_variants')
          .select('*')
          .in('product_id', productIds)
          .order('size'),
        supabase
          .from('product_images')
          .select('*')
          .in('product_id', productIds)
          .order('display_order'),
        supabase
          .from('product_faqs')
          .select('*')
          .in('product_id', productIds)
          .order('display_order'),
      ]);

      const variantsByProduct = new Map<string, any[]>();
      for (const v of (variantsRes.data || [])) {
        const list = variantsByProduct.get(v.product_id) || [];
        list.push(v);
        variantsByProduct.set(v.product_id, list);
      }

      const productImagesByProduct = new Map<string, any[]>();
      const variantImagesByVariant = new Map<string, any[]>();
      for (const img of (imagesRes.data || [])) {
        const imgWithUrl = { ...img, url: getProductImageUrl(img.storage_path, img.uses_new_bucket, 'original') };
        if (img.variant_id) {
          const list = variantImagesByVariant.get(img.variant_id) || [];
          list.push(imgWithUrl);
          variantImagesByVariant.set(img.variant_id, list);
        } else {
          const list = productImagesByProduct.get(img.product_id) || [];
          list.push(imgWithUrl);
          productImagesByProduct.set(img.product_id, list);
        }
      }

      const faqsByProduct = new Map<string, any[]>();
      for (const f of (faqsRes.data || [])) {
        const list = faqsByProduct.get(f.product_id) || [];
        list.push(f);
        faqsByProduct.set(f.product_id, list);
      }

      const productsWithVariantsAndImages = products.map((product: any) => {
        const variants = (variantsByProduct.get(product.id) || []).map((variant: any) => ({
          ...variant,
          images: variantImagesByVariant.get(variant.id) || []
        }));

        return {
          ...product,
          variants,
          images: productImagesByProduct.get(product.id) || [],
          faqs: faqsByProduct.get(product.id) || []
        };
      });

      setProducts(productsWithVariantsAndImages);
      setFilteredProducts(productsWithVariantsAndImages);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMessage({ type: 'error', text: 'Fout bij het ophalen van producten' });
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const generateVariantSlug = (categorySlug: string, name: string, scientificName: string, size: string, type: string): string => {
    const namePart = generateSlug(name);
    const scientificPart = generateSlug(scientificName);
    const sizePart = generateSlug(size.replace(/cm/gi, ''));
    const typePart = generateSlug(type);

    return `${categorySlug}/${namePart}-${scientificPart}-${sizePart}-${typePart}`;
  };

  const generateSKU = (name: string, scientificName: string, size: string, type: string): string => {
    const namePart = generateSlug(name).substring(0, 3).toUpperCase();
    const scientificPart = generateSlug(scientificName).substring(0, 3).toUpperCase();
    const sizePart = size.replace(/[^0-9]/g, '');
    const typePart = type === 'Kluit' ? 'K' : type === 'Pot' ? 'P' : 'KW';
    const timestamp = Date.now().toString().slice(-6);

    return `${namePart}${scientificPart}-${sizePart}${typePart}-${timestamp}`;
  };

  const getPriceTierLabels = (type: 'Kluit' | 'Pot' | 'Kale wortel') => {
    if (type === 'Kale wortel') {
      return {
        tier1: '100-200 stuks',
        tier2: '200-300 stuks',
        tier3: '300-500 stuks',
        tier4: '500+ stuks'
      };
    }
    return {
      tier1: '25-50 stuks',
      tier2: '51-75 stuks',
      tier3: '76-100 stuks',
      tier4: '101+ stuks'
    };
  };

  const handleDescriptionToolbarInsert = (before: string, after?: string) => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.description.substring(start, end);

    const newText = before + selectedText + (after || '');
    const newDescription =
      formData.description.substring(0, start) +
      newText +
      formData.description.substring(end);

    setFormData({ ...formData, description: newDescription });

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleProductImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, productId?: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingImage(true);
      const file = files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `products/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const newImage: ProductImage = {
        storage_path: filePath,
        display_order: productImages.length,
        is_primary: productImages.length === 0,
        alt_text: file.name,
        uses_new_bucket: true
      };

      const imageWithUrl = {
        ...newImage,
        url: getProductImageUrl(filePath, true, 'original')
      };

      setProductImages([...productImages, imageWithUrl]);
      setMessage({ type: 'success', text: 'Afbeelding succesvol geüpload' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Fout bij het uploaden van afbeelding' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVariantImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, variantIndex: number) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingImage(true);
      const uploadedImages: ProductImage[] = [];

      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
        const filePath = `products/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const newImage: ProductImage = {
          storage_path: filePath,
          display_order: (variants[variantIndex].images?.length || 0) + uploadedImages.length,
          is_primary: (variants[variantIndex].images?.length || 0) === 0 && uploadedImages.length === 0,
          alt_text: file.name,
          variant_id: variants[variantIndex].id || null,
          uses_new_bucket: true
        };

        uploadedImages.push({
          ...newImage,
          url: getProductImageUrl(filePath, true, 'original')
        });
      }

      const updatedVariants = [...variants];
      updatedVariants[variantIndex].images = [
        ...(updatedVariants[variantIndex].images || []),
        ...uploadedImages
      ];
      setVariants(updatedVariants);
      setMessage({ type: 'success', text: 'Afbeeldingen succesvol geüpload' });
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage({ type: 'error', text: 'Fout bij het uploaden van afbeeldingen' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveProductImage = (index: number) => {
    const updatedImages = productImages.filter((_, i) => i !== index);
    if (productImages[index].is_primary && updatedImages.length > 0) {
      updatedImages[0].is_primary = true;
    }
    updatedImages.forEach((img, i) => {
      img.display_order = i;
    });
    setProductImages(updatedImages);
  };

  const handleRemoveVariantImage = (variantIndex: number, imageIndex: number) => {
    const updatedVariants = [...variants];
    const variantImages = updatedVariants[variantIndex].images || [];
    const updatedImages = variantImages.filter((_, i) => i !== imageIndex);

    if (variantImages[imageIndex].is_primary && updatedImages.length > 0) {
      updatedImages[0].is_primary = true;
    }

    updatedImages.forEach((img, i) => {
      img.display_order = i;
    });

    updatedVariants[variantIndex].images = updatedImages;
    setVariants(updatedVariants);
  };

  const handleMoveProductImage = (index: number, direction: 'up' | 'down') => {
    const updatedImages = [...productImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updatedImages[index], updatedImages[newIndex]] = [updatedImages[newIndex], updatedImages[index]];
    updatedImages.forEach((img, i) => {
      img.display_order = i;
    });
    setProductImages(updatedImages);
  };

  const handleMoveVariantImage = (variantIndex: number, imageIndex: number, direction: 'up' | 'down') => {
    const updatedVariants = [...variants];
    const variantImages = [...(updatedVariants[variantIndex].images || [])];
    const newIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;

    [variantImages[imageIndex], variantImages[newIndex]] = [variantImages[newIndex], variantImages[imageIndex]];

    variantImages.forEach((img, i) => {
      img.display_order = i;
    });

    updatedVariants[variantIndex].images = variantImages;
    setVariants(updatedVariants);
  };

  const handleSetPrimaryImage = (index: number) => {
    const updatedImages = productImages.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));
    setProductImages(updatedImages);
  };

  const handleSetPrimaryVariantImage = (variantIndex: number, imageIndex: number) => {
    const updatedVariants = [...variants];
    updatedVariants[variantIndex].images = updatedVariants[variantIndex].images?.map((img, i) => ({
      ...img,
      is_primary: i === imageIndex
    }));
    setVariants(updatedVariants);
  };

  const handleOpenDialog = (product: Product | null = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        scientific_name: product.scientific_name || '',
        description: product.description || '',
        category_id: product.category_id || '',
        geslacht: product.geslacht || '',
        sort: product.sort || '',
        subcategory_id: product.subcategory_id || '',
        standplaats: product.standplaats || [],
        groei_per_jaar: product.groei_per_jaar || '',
        snoeien: product.snoeien || '',
        wintergroen: product.wintergroen,
        winterhard: product.winterhard,
        maximale_hoogte: product.maximale_hoogte || '',
        btw_percentage: product.btw_percentage || 9,
        featured: product.featured,
        active: product.active,
        is_simple_product: product.is_simple_product,
        simple_price: product.simple_price || 0,
        simple_sku: product.simple_sku || '',
        supplier: product.supplier || 'Kwekerij',
        transport_method: product.transport_method || 'kuubkist',
        available_from: product.available_from || null
      });
      setVariants(product.variants || []);
      setProductImages(product.images || []);
      setProductFAQs(product.faqs || []);
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        scientific_name: '',
        description: '',
        category_id: '',
        geslacht: '',
        sort: '',
        standplaats: [],
        groei_per_jaar: '',
        snoeien: '',
        wintergroen: false,
        winterhard: false,
        maximale_hoogte: '',
        btw_percentage: 9,
        featured: false,
        active: true,
        is_simple_product: false,
        simple_price: 0,
        simple_sku: '',
        supplier: 'Kwekerij',
        transport_method: 'kuubkist',
        available_from: null
      });
      setVariants([]);
      setProductImages([]);
      setProductFAQs([]);
    }
    setShowAddVariantForm(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
    setFormData({
      name: '',
      scientific_name: '',
      description: '',
      category_id: '',
      geslacht: '',
      sort: '',
      standplaats: [],
      groei_per_jaar: '',
      snoeien: '',
      wintergroen: false,
      winterhard: false,
      maximale_hoogte: '',
      btw_percentage: 9,
      featured: false,
      active: true,
      is_simple_product: false,
      simple_price: 0,
      simple_sku: '',
      supplier: 'Kwekerij',
      transport_method: 'kuubkist',
      available_from: null
    });
    setVariants([]);
    setProductImages([]);
    setProductFAQs([]);
    setShowAddVariantForm(false);
    setNewVariant({
      size: '',
      type: 'Kluit',
      price_tier_1: 0,
      price_tier_2: null,
      price_tier_3: null,
      price_tier_4: null,
      minimum_order_quantity: 50,
      stock_quantity: 500,
      sku: '',
      planten_per_meter: 2.5,
      price_rounding_increment_cents: 5,
      supplier: 'Kwekerij',
      transport_method: 'kuubkist',
      active: true
    });
    setNewFAQ({
      question: '',
      answer: '',
      active: true
    });
  };

  const handleAddVariant = () => {
    if (!newVariant.size || !newVariant.type) {
      setMessage({ type: 'error', text: 'Vul minimaal grootte en type in' });
      return;
    }

    const category = categories.find(c => c.id === formData.category_id);
    const categorySlug = category?.slug || 'product';

    const slug = generateVariantSlug(
      categorySlug,
      formData.name,
      formData.scientific_name,
      newVariant.size,
      newVariant.type
    );

    const sku = newVariant.sku || generateSKU(
      formData.name,
      formData.scientific_name,
      newVariant.size,
      newVariant.type
    );

    const variant: ProductVariant = {
      size: newVariant.size,
      type: newVariant.type as 'Kluit' | 'Pot' | 'Kale wortel',
      price_tier_1: newVariant.price_tier_1 || 0,
      price_tier_2: newVariant.price_tier_2,
      price_tier_3: newVariant.price_tier_3,
      price_tier_4: newVariant.price_tier_4,
      minimum_order_quantity: newVariant.minimum_order_quantity ?? 50,
      stock_quantity: newVariant.stock_quantity ?? 500,
      sku,
      slug,
      planten_per_meter: newVariant.planten_per_meter ?? 2.5,
      price_rounding_increment_cents: newVariant.price_rounding_increment_cents ?? 5,
      supplier: newVariant.supplier || 'Kwekerij',
      transport_method: newVariant.transport_method || 'kuubkist',
      active: newVariant.active !== undefined ? newVariant.active : true,
      available_from: newVariant.available_from || null
    };

    setVariants([...variants, variant]);
    setNewVariant({
      size: '',
      type: 'Kluit',
      price_tier_1: 0,
      price_tier_2: null,
      price_tier_3: null,
      price_tier_4: null,
      minimum_order_quantity: 50,
      stock_quantity: 500,
      sku: '',
      planten_per_meter: 2.5,
      price_rounding_increment_cents: 5,
      supplier: 'Kwekerij',
      transport_method: 'kuubkist',
      active: true,
      available_from: null
    });
    setShowAddVariantForm(false);
  };

  const handleEditVariant = (index: number) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, isEditing: true } : v));
  };

  const handleSaveVariantEdit = (index: number) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, isEditing: false } : v));
  };

  const handleCancelVariantEdit = (index: number) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, isEditing: false } : v));
  };

  const handleUpdateVariant = (index: number, field: keyof ProductVariant, value: any) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleAddFAQ = () => {
    if (!newFAQ.question || !newFAQ.answer) {
      setMessage({ type: 'error', text: 'Vul zowel vraag als antwoord in' });
      return;
    }

    const faq: ProductFAQ = {
      question: newFAQ.question,
      answer: newFAQ.answer,
      display_order: productFAQs.length,
      active: newFAQ.active !== undefined ? newFAQ.active : true
    };

    setProductFAQs([...productFAQs, faq]);
    setNewFAQ({
      question: '',
      answer: '',
      active: true
    });
  };

  const handleEditFAQ = (index: number) => {
    setProductFAQs(prev => prev.map((f, i) => i === index ? { ...f, isEditing: true } : f));
  };

  const handleSaveFAQEdit = (index: number) => {
    setProductFAQs(prev => prev.map((f, i) => i === index ? { ...f, isEditing: false } : f));
  };

  const handleCancelFAQEdit = (index: number) => {
    setProductFAQs(prev => prev.map((f, i) => i === index ? { ...f, isEditing: false } : f));
  };

  const handleUpdateFAQ = (index: number, field: keyof ProductFAQ, value: any) => {
    setProductFAQs(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const handleRemoveFAQ = (index: number) => {
    setProductFAQs(productFAQs.filter((_, i) => i !== index));
  };

  const handleMoveFAQ = (index: number, direction: 'up' | 'down') => {
    const updatedFAQs = [...productFAQs];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updatedFAQs[index], updatedFAQs[newIndex]] = [updatedFAQs[newIndex], updatedFAQs[index]];
    updatedFAQs.forEach((faq, i) => {
      faq.display_order = i;
    });
    setProductFAQs(updatedFAQs);
  };

  const handleSaveProduct = async (closeAfterSave: boolean = true) => {
    try {
      if (!formData.name) {
        setMessage({ type: 'error', text: 'Productnaam is verplicht' });
        return;
      }

      if (!formData.category_id) {
        setMessage({ type: 'error', text: 'Categorie is verplicht' });
        return;
      }

      if (!formData.is_simple_product && variants.length > 0) {
        const skus = variants.map(v => v.sku).filter(Boolean);
        const duplicates = skus.filter((s, i) => skus.indexOf(s) !== i);
        if (duplicates.length > 0) {
          setMessage({ type: 'error', text: `Dubbele SKU gevonden: ${[...new Set(duplicates)].join(', ')}. Elke variant moet een unieke SKU hebben.` });
          return;
        }
      }

      const category = categories.find(c => c.id === formData.category_id);
      const categorySlug = category?.slug || 'product';
      const productSlug = generateSlug(`${formData.name}-${formData.scientific_name}`);
      const baseSlug = `${categorySlug}/${productSlug}`;

      const productData = {
        name: formData.name,
        scientific_name: formData.scientific_name || null,
        description: formData.description || null,
        category_id: formData.category_id,
        base_slug: baseSlug,
        geslacht: formData.geslacht || null,
        sort: formData.sort || null,
        subcategory_id: formData.subcategory_id || null,
        standplaats: formData.standplaats.length > 0 ? formData.standplaats : null,
        groei_per_jaar: formData.groei_per_jaar || null,
        snoeien: formData.snoeien || null,
        wintergroen: formData.wintergroen,
        winterhard: formData.winterhard,
        maximale_hoogte: formData.maximale_hoogte || null,
        btw_percentage: formData.btw_percentage || 9,
        featured: formData.featured,
        active: formData.active,
        is_simple_product: formData.is_simple_product,
        simple_price: formData.is_simple_product ? formData.simple_price : null,
        simple_sku: formData.is_simple_product ? formData.simple_sku : null,
        supplier: formData.supplier,
        transport_method: formData.transport_method,
        available_from: formData.is_simple_product ? (formData.available_from || null) : null
      };

      let productId: string;

      if (selectedProduct) {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', selectedProduct.id);

        if (error) throw error;
        productId = selectedProduct.id;

        const existingVariantIds = variants.filter(v => v.id).map(v => v.id!);
        const { error: delRemovedVariantsErr } = await supabase
          .from('product_variants')
          .delete()
          .eq('product_id', productId)
          .not('id', 'in', existingVariantIds.length > 0 ? `(${existingVariantIds.join(',')})` : '(00000000-0000-0000-0000-000000000000)');
        if (delRemovedVariantsErr) throw delRemovedVariantsErr;
        const { error: delImagesErr } = await supabase.from('product_images').delete().eq('product_id', productId);
        if (delImagesErr) throw delImagesErr;
        const { error: delFaqsErr } = await supabase.from('product_faqs').delete().eq('product_id', productId);
        if (delFaqsErr) throw delFaqsErr;
      } else {
        const { data, error } = await supabase
          .from('products')
          .insert(productData)
          .select()
          .single();

        if (error) throw error;
        productId = data.id;
      }

      if (!formData.is_simple_product && variants.length > 0) {
        const variantRecords = variants.map(variant => ({
          product_id: productId,
          size: variant.size,
          type: variant.type,
          price_tier_1: variant.price_tier_1,
          price_tier_2: variant.price_tier_2,
          price_tier_3: variant.price_tier_3,
          price_tier_4: variant.price_tier_4,
          minimum_order_quantity: variant.minimum_order_quantity,
          stock_quantity: variant.stock_quantity,
          sku: variant.sku,
          slug: variant.slug,
          planten_per_meter: variant.planten_per_meter,
          supplier: variant.supplier,
          transport_method: variant.transport_method,
          purchase_price_cents: variant.purchase_price_cents || null,
          plants_per_box: variant.plants_per_box || null,
          boxes_per_pallet_position: variant.boxes_per_pallet_position || 2,
          box_cost_cents: variant.box_cost_cents || null,
          price_rounding_increment_cents: variant.price_rounding_increment_cents ?? 5,
          available_from: variant.available_from || null,
          active: variant.active
        }));

        const existingVariants = variants.filter(v => v.id);
        const newVariants = variants.filter(v => !v.id);

        let savedVariants: any[] = [];

        if (existingVariants.length > 0) {
          const { data: updated, error: updateError } = await supabase
            .from('product_variants')
            .upsert(
              existingVariants.map((v, i) => ({ id: v.id, ...variantRecords[i] })),
              { onConflict: 'id' }
            )
            .select();
          if (updateError) throw updateError;
          if (updated) savedVariants = savedVariants.concat(updated);
        }

        if (newVariants.length > 0) {
          const newRecords = newVariants.map(v => {
            const idx = variants.indexOf(v);
            return variantRecords[idx];
          });
          const { data: inserted, error: insertError } = await supabase
            .from('product_variants')
            .insert(newRecords)
            .select();
          if (insertError) throw insertError;
          if (inserted) savedVariants = savedVariants.concat(inserted);
        }

        const variantMap = new Map<string, any>();
        savedVariants.forEach(v => variantMap.set(v.id, v));

        for (let i = 0; i < variants.length; i++) {
          const variant = variants[i];
          const savedVariant = variant.id ? variantMap.get(variant.id) : savedVariants.find(v => v.sku === variant.sku && v.size === variant.size && v.type === variant.type);

          if (variant.images && variant.images.length > 0) {
            const variantImagesToInsert = variant.images.map(img => ({
              product_id: productId,
              variant_id: savedVariant.id,
              storage_path: img.storage_path,
              display_order: img.display_order,
              is_primary: img.is_primary,
              alt_text: img.alt_text,
              uses_new_bucket: true
            }));

            const { error: variantImagesError } = await supabase
              .from('product_images')
              .insert(variantImagesToInsert);

            if (variantImagesError) throw variantImagesError;
          }
        }
      }

      if (productImages.length > 0) {
        const imagesToInsert = productImages.map(img => ({
          product_id: productId,
          storage_path: img.storage_path,
          display_order: img.display_order,
          is_primary: img.is_primary,
          alt_text: img.alt_text,
          uses_new_bucket: true
        }));

        const { error: imagesError } = await supabase
          .from('product_images')
          .insert(imagesToInsert);

        if (imagesError) throw imagesError;

        const primaryImage = productImages.find(img => img.is_primary) || productImages[0];
        if (primaryImage) {
          const primaryImageUrl = getProductImageUrl(primaryImage.storage_path, true, 'original');
          const { error: imageUrlError } = await supabase
            .from('products')
            .update({ image_url: primaryImageUrl })
            .eq('id', productId);

          if (imageUrlError) throw imageUrlError;
        }
      } else {
        const { error: clearImageUrlError } = await supabase
          .from('products')
          .update({ image_url: null })
          .eq('id', productId);

        if (clearImageUrlError) throw clearImageUrlError;
      }

      if (productFAQs.length > 0) {
        const faqsToInsert = productFAQs.map(faq => ({
          product_id: productId,
          question: faq.question,
          answer: faq.answer,
          display_order: faq.display_order,
          active: faq.active
        }));

        const { error: faqsError } = await supabase
          .from('product_faqs')
          .insert(faqsToInsert);

        if (faqsError) throw faqsError;
      }

      setMessage({ type: 'success', text: selectedProduct ? 'Product bijgewerkt' : 'Product toegevoegd' });
      if (closeAfterSave) {
        handleCloseDialog();
      }
      fetchProducts();
    } catch (error: any) {
      console.error('Error saving product:', error);
      const msg = error?.message || error?.details || '';
      if (msg.includes('product_variants_sku_key') || msg.includes('duplicate key')) {
        const duplicateSkus = variants.map(v => v.sku).filter((s, i, a) => s && a.indexOf(s) !== i);
        if (duplicateSkus.length > 0) {
          setMessage({ type: 'error', text: `Dubbele SKU gevonden binnen dit product: ${duplicateSkus.join(', ')}. Elke variant moet een unieke SKU hebben.` });
        } else {
          setMessage({ type: 'error', text: 'Een van de SKU-codes is al in gebruik bij een ander product. Controleer de SKU-codes van de varianten.' });
        }
      } else {
        setMessage({ type: 'error', text: `Fout bij het opslaan: ${msg || 'onbekende fout'}` });
      }
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const { data: images } = await supabase
        .from('product_images')
        .select('storage_path, uses_new_bucket')
        .eq('product_id', productToDelete.id);

      if (images && images.length > 0) {
        for (const image of images) {
          if (image.uses_new_bucket) {
            await supabase.storage
              .from('product-images')
              .remove([image.storage_path]);
          }
        }
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productToDelete.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Product verwijderd' });
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage({ type: 'error', text: 'Fout bij het verwijderen van product' });
    }
  };

  const handleToggleActive = async (productId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ active: !currentStatus })
        .eq('id', productId);

      if (error) throw error;
      fetchProducts();
    } catch (error) {
      console.error('Error toggling active status:', error);
      setMessage({ type: 'error', text: 'Fout bij het wijzigen van actieve status' });
    }
  };

  const getTotalImageCount = (product: Product): number => {
    let count = product.images?.length || 0;
    if (product.variants) {
      product.variants.forEach(variant => {
        count += variant.images?.length || 0;
      });
    }
    return count;
  };

  const toggleRowExpansion = (productId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedRows(newExpanded);
  };

  const handleToggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleStartEditVariant = (productId: string, variantIndex: number, currentValue: number) => {
    setEditingValue(currentValue.toString());
    setEditingVariant({ productId, variantIndex, field: 'stock' });
  };

  const handleSaveVariantInlineEdit = async (product: Product, variantIndex: number) => {
    if (!editingVariant) return;

    try {
      const variant = product.variants?.[variantIndex];
      if (!variant || !variant.id) return;

      const newValue = parseFloat(editingValue);
      if (isNaN(newValue)) {
        setMessage({ type: 'error', text: 'Ongeldige waarde' });
        return;
      }

      if (editingVariant.field === 'stock') {
        const { error } = await supabase
          .from('product_variants')
          .update({ stock_quantity: Math.floor(newValue) })
          .eq('id', variant.id);

        if (error) throw error;
      }

      setEditingVariant(null);
      setEditingValue('');
      fetchProducts();
      setMessage({ type: 'success', text: 'Variant bijgewerkt' });
    } catch (error) {
      console.error('Error updating variant:', error);
      setMessage({ type: 'error', text: 'Fout bij het bijwerken van variant' });
    }
  };

  const handleCancelVariantInlineEdit = () => {
    setEditingVariant(null);
    setEditingValue('');
  };

  const handleRowClick = (productId: string, hasVariants: boolean, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('[role="switch"]')
    ) {
      return;
    }
    if (hasVariants) {
      toggleRowExpansion(productId);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Assortiment" />
        <h1 className="text-4xl font-bold text-jv-primary mb-6">Assortiment</h1>
        <div className="bg-white rounded-2xl shadow-md overflow-hidden p-8 text-center">
          <div className="w-12 h-12 border-4 border-jv-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Assortiment" />

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Assortiment</h1>
            <p className="text-gray-600">Beheer producten met varianten en afbeeldingen</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nieuw product
          </Button>
        </div>

        {message && (
          <Alert className={`mb-6 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <Label htmlFor="search">Zoeken</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="search"
                type="text"
                placeholder="Zoek op naam, wetenschappelijke naam of categorie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-64">
            <Label htmlFor="category-filter">Filter op categorie</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger id="category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Package className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-2">Geen producten gevonden</p>
                <p className="text-gray-400">Klik op "Nieuw product" om een product toe te voegen</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead className="w-24"></TableHead>
                    <TableHead className="cursor-pointer" onClick={handleToggleSortOrder}>
                      <div className="flex items-center gap-2">
                        Productnaam
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead>Varianten</TableHead>
                    <TableHead className="w-20">Bestelbaar</TableHead>
                    <TableHead className="w-32 text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map(product => (
                    <>
                      <TableRow
                        key={product.id}
                        className={`${!product.active ? 'opacity-60' : ''} ${product.variants && product.variants.length > 0 ? 'cursor-pointer' : ''}`}
                        onClick={(e) => handleRowClick(product.id, !!(product.variants && product.variants.length > 0), e)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {product.variants && product.variants.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-0 h-8 w-8"
                              onClick={() => toggleRowExpansion(product.id)}
                            >
                              {expandedRows.has(product.id) ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images.find(img => img.is_primary)?.url || product.images[0]?.url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent && !parent.querySelector('.fallback-icon')) {
                                    const fallback = document.createElement('div');
                                    fallback.className = 'fallback-icon w-full h-full flex items-center justify-center';
                                    fallback.innerHTML = '<svg class="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>';
                                    parent.appendChild(fallback);
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-gray-300" />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{product.name}</div>
                            {product.scientific_name && (
                              <div className="text-sm italic text-gray-500">{product.scientific_name}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge style={{ backgroundColor: '#ffaf2e', color: '#153907' }}>
                            {product.product_categories?.name || 'Geen categorie'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {product.is_simple_product ? (
                            <Badge variant="outline" className="bg-[#e7e1d6]/60 border-[#cfcac2] text-[#1e2b22]">
                              Enkel product
                            </Badge>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Layers className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">{product.variants?.length || 0}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Switch
                            checked={product.active}
                            onCheckedChange={() => handleToggleActive(product.id, product.active)}
                          />
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(product)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {!product.active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setProductToDelete(product);
                                  setIsDeleteDialogOpen(true);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(product.id) && product.variants && product.variants.map((variant, index) => {
                        const isEditingStock = editingVariant?.productId === product.id && editingVariant?.variantIndex === index && editingVariant?.field === 'stock';
                        const tierLabels = getPriceTierLabels(variant.type);
                        const tiers = [
                          { label: 'T1', qty: tierLabels.tier1, price: variant.price_tier_1 },
                          { label: 'T2', qty: tierLabels.tier2, price: variant.price_tier_2 },
                          { label: 'T3', qty: tierLabels.tier3, price: variant.price_tier_3 },
                          { label: 'T4', qty: tierLabels.tier4, price: variant.price_tier_4 },
                        ];

                        return (
                          <TableRow key={`${product.id}-variant-${index}`} className="bg-[#fcfaf6]">
                            <TableCell></TableCell>
                            <TableCell>
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 ml-4">
                                {variant.images && variant.images.length > 0 ? (
                                  <img
                                    src={variant.images.find(img => img.is_primary)?.url || variant.images[0]?.url}
                                    alt={`${product.name} ${variant.size}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const parent = e.currentTarget.parentElement;
                                      if (parent && !parent.querySelector('.fallback-icon')) {
                                        const fallback = document.createElement('div');
                                        fallback.className = 'fallback-icon w-full h-full flex items-center justify-center';
                                        fallback.innerHTML = '<svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>';
                                        parent.appendChild(fallback);
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-6 h-6 text-gray-400" />
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm ml-8">
                                <div className="font-medium">{variant.size} - {variant.type}</div>
                                <div className="text-xs text-gray-500">{variant.supplier}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1.5">
                                {tiers.map((tier, i) => (
                                  <div key={i} className="flex flex-col items-center">
                                    <Badge className="bg-[#e7e1d6] text-[#1e2b22] text-[10px] px-1.5 py-0 mb-0.5" variant="secondary">
                                      {tier.label}
                                    </Badge>
                                    <span className="text-xs font-medium text-gray-700">€{tier.price?.toFixed(2) ?? '—'}</span>
                                    <span className="text-[9px] text-gray-400">{tier.qty}</span>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              {isEditingStock ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveVariantInlineEdit(product, index);
                                      } else if (e.key === 'Escape') {
                                        handleCancelVariantInlineEdit();
                                      }
                                    }}
                                    className="w-20 h-7 text-xs"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSaveVariantInlineEdit(product, index)}
                                    className="h-6 w-6 p-0"
                                  >
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelVariantInlineEdit}
                                    className="h-6 w-6 p-0"
                                  >
                                    <X className="w-3 h-3 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <div
                                  className="text-sm cursor-pointer hover:bg-gray-200 px-2 py-1 rounded inline-block"
                                  onClick={() => handleStartEditVariant(product.id, index, variant.stock_quantity || 0)}
                                >
                                  Voorraad: <span className="font-medium">{variant.stock_quantity}</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={variant.active}
                                onCheckedChange={() => {
                                  const updatedVariants = [...(product.variants || [])];
                                  updatedVariants[index].active = !variant.active;
                                }}
                                disabled
                              />
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col overflow-hidden" aria-describedby={undefined}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {selectedProduct ? 'Product bewerken' : 'Nieuw product toevoegen'}
              </DialogTitle>
              <div className="flex gap-2">
                {getTotalImageCount({ ...formData, id: '', created_at: '', updated_at: '', variants, images: productImages, faqs: productFAQs } as Product) > 0 && (
                  <Badge variant="secondary">
                    <ImageIcon className="w-3 h-3 mr-1" />
                    {getTotalImageCount({ ...formData, id: '', created_at: '', updated_at: '', variants, images: productImages, faqs: productFAQs } as Product)} afbeeldingen
                  </Badge>
                )}
                {variants.length > 0 && (
                  <Badge variant="secondary">
                    <Layers className="w-3 h-3 mr-1" />
                    {variants.length} varianten
                  </Badge>
                )}
                {productFAQs.length > 0 && (
                  <Badge variant="secondary">
                    <HelpCircle className="w-3 h-3 mr-1" />
                    {productFAQs.length} FAQs
                  </Badge>
                )}
              </div>
            </div>
            <DialogDescription>
              Vul de productinformatie, varianten en afbeeldingen in
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <Tabs defaultValue="product" className="mt-6">
            <TabsList className={`grid w-full ${!formData.is_simple_product ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="product">Product details</TabsTrigger>
              {!formData.is_simple_product && (
                <TabsTrigger value="variants">
                  <Calculator className="w-3 h-3 mr-1" />
                  Varianten & Prijzen ({variants.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="faqs">
                FAQs ({productFAQs.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="product" className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="product-name">Productnaam *</Label>
                  <Input
                    id="product-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Bijv. Taxus haag"
                  />
                </div>
                <div>
                  <Label htmlFor="scientific-name">Wetenschappelijke naam</Label>
                  <Input
                    id="scientific-name"
                    value={formData.scientific_name}
                    onChange={(e) => setFormData({ ...formData, scientific_name: e.target.value })}
                    placeholder="Bijv. Taxus baccata"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Categorie *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value, subcategory_id: '' })}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Selecteer een categorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="subcategory">Subcategorie</Label>
                  <Select
                    value={formData.subcategory_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, subcategory_id: value === 'none' ? '' : value })}
                    disabled={!formData.category_id}
                  >
                    <SelectTrigger id="subcategory">
                      <SelectValue placeholder="Geen subcategorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen subcategorie</SelectItem>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.category_id && subcategories.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Nog geen subcategorieën voor deze categorie.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-simple-product"
                    checked={formData.is_simple_product}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_simple_product: checked, transport_method: checked ? 'tuinplanten' : formData.transport_method })}
                  />
                  <Label htmlFor="is-simple-product">Enkel product</Label>
                </div>
              </div>

              {formData.is_simple_product && (
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/50 p-5 shadow-sm">
                  <div className="mb-4 flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">Instellingen voor enkel product</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="simple-price" className="text-slate-700">Prijs</Label>
                      <Input
                        id="simple-price"
                        type="number"
                        step="0.01"
                        value={formData.simple_price === 0 ? '' : formData.simple_price}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, simple_price: val === '' ? 0 : parseFloat(val) || 0 });
                        }}
                        placeholder="0,00"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="simple-sku" className="text-slate-700">
                        SKU <span className="text-xs font-normal text-slate-400">(optioneel)</span>
                      </Label>
                      <Input
                        id="simple-sku"
                        value={formData.simple_sku}
                        onChange={(e) => setFormData({ ...formData, simple_sku: e.target.value })}
                        placeholder="Bijv. GROND-001"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="supplier" className="text-slate-700">Leverancier</Label>
                      <Select
                        value={formData.supplier}
                        onValueChange={(value: 'Kwekerij' | 'Overig') => setFormData({ ...formData, supplier: value })}
                      >
                        <SelectTrigger id="supplier" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kwekerij">Kwekerij</SelectItem>
                          <SelectItem value="Overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="transport-method" className="text-slate-700">Transport methode</Label>
                      <Select
                        value={formData.transport_method}
                        onValueChange={(value: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none') => setFormData({ ...formData, transport_method: value })}
                      >
                        <SelectTrigger id="transport-method" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tuinplanten">Tuinplanten (€9,95)</SelectItem>
                          <SelectItem value="kuubkist">Kuubkist</SelectItem>
                          <SelectItem value="halve_kuubkist">Halve Kuubkist</SelectItem>
                          <SelectItem value="pallet">Pallet</SelectItem>
                          <SelectItem value="none">Geen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {categories.find(c => c.id === formData.category_id)?.slug !== 'bomen' && (
                      <div>
                        <Label className="flex items-center gap-2">
                          <CalendarClock className="w-4 h-4" />
                          Leverbaar vanaf
                        </Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Switch
                            checked={!!formData.available_from}
                            onCheckedChange={(checked) => setFormData({ ...formData, available_from: checked ? new Date().toISOString().split('T')[0] : null })}
                          />
                          <Input
                            type="date"
                            value={formData.available_from ? formData.available_from.split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, available_from: e.target.value || null })}
                            disabled={!formData.available_from}
                            className="w-48 bg-white"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Product blijft zichtbaar maar kan pas besteld worden vanaf deze datum.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="description">Beschrijving</Label>
                <SimpleMarkdownToolbar onInsert={handleDescriptionToolbarInsert} />
                <Textarea
                  ref={descriptionTextareaRef}
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  placeholder="Beschrijving in markdown formaat (zonder heading tags)"
                />
              </div>

              <div>
                <Label htmlFor="geslacht">Geslacht (botanisch)</Label>
                <Input
                  id="geslacht"
                  value={formData.geslacht}
                  onChange={(e) => setFormData({ ...formData, geslacht: e.target.value })}
                  placeholder="Bijv. Prunus"
                />
              </div>

              <div>
                <Label>Standplaats</Label>
                <div className="flex gap-6 mt-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="standplaats-zon"
                      checked={formData.standplaats.includes('zon')}
                      onCheckedChange={(checked) => {
                        const newStandplaats = checked
                          ? [...formData.standplaats, 'zon']
                          : formData.standplaats.filter(s => s !== 'zon');
                        setFormData({ ...formData, standplaats: newStandplaats });
                      }}
                    />
                    <Label htmlFor="standplaats-zon" className="cursor-pointer">Zon</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="standplaats-halfschaduw"
                      checked={formData.standplaats.includes('halfschaduw')}
                      onCheckedChange={(checked) => {
                        const newStandplaats = checked
                          ? [...formData.standplaats, 'halfschaduw']
                          : formData.standplaats.filter(s => s !== 'halfschaduw');
                        setFormData({ ...formData, standplaats: newStandplaats });
                      }}
                    />
                    <Label htmlFor="standplaats-halfschaduw" className="cursor-pointer">Halfschaduw</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="standplaats-schaduw"
                      checked={formData.standplaats.includes('schaduw')}
                      onCheckedChange={(checked) => {
                        const newStandplaats = checked
                          ? [...formData.standplaats, 'schaduw']
                          : formData.standplaats.filter(s => s !== 'schaduw');
                        setFormData({ ...formData, standplaats: newStandplaats });
                      }}
                    />
                    <Label htmlFor="standplaats-schaduw" className="cursor-pointer">Schaduw</Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="groei-per-jaar">Groei per jaar</Label>
                  <Input
                    id="groei-per-jaar"
                    value={formData.groei_per_jaar}
                    onChange={(e) => setFormData({ ...formData, groei_per_jaar: e.target.value })}
                    placeholder="Bijv. 20-40cm"
                  />
                </div>
                <div>
                  <Label htmlFor="snoeien">Snoeien</Label>
                  <Input
                    id="snoeien"
                    value={formData.snoeien}
                    onChange={(e) => setFormData({ ...formData, snoeien: e.target.value })}
                    placeholder="Bijv. 1-2x per jaar"
                  />
                </div>
                <div>
                  <Label htmlFor="maximale-hoogte">Maximale hoogte</Label>
                  <Input
                    id="maximale-hoogte"
                    value={formData.maximale_hoogte}
                    onChange={(e) => setFormData({ ...formData, maximale_hoogte: e.target.value })}
                    placeholder="Bijv. 200-400cm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="wintergroen"
                    checked={formData.wintergroen}
                    onCheckedChange={(checked) => setFormData({ ...formData, wintergroen: checked })}
                  />
                  <Label htmlFor="wintergroen">Wintergroen</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="winterhard"
                    checked={formData.winterhard}
                    onCheckedChange={(checked) => setFormData({ ...formData, winterhard: checked })}
                  />
                  <Label htmlFor="winterhard">Winterhard</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  />
                  <Label htmlFor="featured">Featured</Label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="btw-percentage">BTW percentage</Label>
                  <Select
                    value={formData.btw_percentage.toString()}
                    onValueChange={(value) => setFormData({ ...formData, btw_percentage: parseInt(value) })}
                  >
                    <SelectTrigger id="btw-percentage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9">9%</SelectItem>
                      <SelectItem value="21">21%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 pt-8">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label htmlFor="active">Actief</Label>
                </div>
              </div>

              <div>
                <Label>Product afbeeldingen</Label>
                <div className="mt-2 grid grid-cols-4 gap-4">
                  {productImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.url}
                        alt={image.alt_text}
                        className={`w-full h-32 object-cover rounded-lg ${image.is_primary ? 'ring-2 ring-[#1e2b22]' : ''}`}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent && !parent.querySelector('.fallback-icon')) {
                            const fallback = document.createElement('div');
                            fallback.className = 'fallback-icon w-full h-32 rounded-lg bg-gray-100 flex items-center justify-center';
                            fallback.innerHTML = '<svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>';
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {index > 0 && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleMoveProductImage(index, 'up')}
                            className="h-6 w-6 p-0"
                          >
                            <MoveUp className="w-3 h-3" />
                          </Button>
                        )}
                        {index < productImages.length - 1 && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleMoveProductImage(index, 'down')}
                            className="h-6 w-6 p-0"
                          >
                            <MoveDown className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRemoveProductImage(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      {!image.is_primary && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleSetPrimaryImage(index)}
                          className="absolute bottom-2 left-2 h-6 text-xs"
                        >
                          Primair maken
                        </Button>
                      )}
                      {image.is_primary && (
                        <Badge className="absolute bottom-2 left-2 bg-[#1e2b22] text-white">
                          Primair
                        </Badge>
                      )}
                    </div>
                  ))}
                  <label className="w-full h-32 border-2 border-dashed border-[#cfcac2] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#1e2b22] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProductImageUpload}
                      disabled={uploadingImage}
                    />
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <span className="text-sm text-gray-500">Upload</span>
                    </div>
                  </label>
                </div>
              </div>
            </TabsContent>

            {!formData.is_simple_product && (
              <TabsContent value="variants" className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Varianten beheren</h3>
                  <Button
                    onClick={() => setShowAddVariantForm(!showAddVariantForm)}
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Variant toevoegen
                  </Button>
                </div>

                {showAddVariantForm && (
                  <Card className="p-4 bg-gray-50">
                    <h4 className="font-semibold mb-4">Nieuwe variant</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Grootte *</Label>
                        <Input
                          value={newVariant.size}
                          onChange={(e) => setNewVariant({ ...newVariant, size: e.target.value })}
                          placeholder="Bijv. 80-100cm"
                        />
                      </div>
                      <div>
                        <Label>Type *</Label>
                        <Select
                          value={newVariant.type}
                          onValueChange={(value: 'Kluit' | 'Pot' | 'Kale wortel') => setNewVariant({ ...newVariant, type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Kluit">Kluit</SelectItem>
                            <SelectItem value="Pot">Pot</SelectItem>
                            <SelectItem value="Kale wortel">Kale wortel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <VariantPricingCalculator
                          productId={selectedProduct?.id || null}
                          btwPercentage={formData.btw_percentage || 9}
                          variant={newVariant as ProductVariant}
                          variantIndex={-1}
                          onUpdatePrices={(_index, prices) => {
                            setNewVariant({
                              ...newVariant,
                              price_tier_1: prices.price_tier_1 || 0,
                              price_tier_2: prices.price_tier_2,
                              price_tier_3: prices.price_tier_3,
                              price_tier_4: prices.price_tier_4,
                            });
                          }}
                          onUpdatePricingFields={(_index, fields) => {
                            setNewVariant({ ...newVariant, ...fields });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Minimale bestelhoeveelheid</Label>
                        <Input
                          type="number"
                          step={50}
                          min={0}
                          value={newVariant.minimum_order_quantity}
                          onChange={(e) => setNewVariant({ ...newVariant, minimum_order_quantity: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Voorraad</Label>
                        <Input
                          type="number"
                          min={0}
                          value={newVariant.stock_quantity}
                          onChange={(e) => setNewVariant({ ...newVariant, stock_quantity: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>SKU</Label>
                        <Input
                          value={newVariant.sku}
                          onChange={(e) => setNewVariant({ ...newVariant, sku: e.target.value })}
                          placeholder="Optioneel (wordt automatisch gegenereerd)"
                        />
                      </div>
                      <div>
                        <Label>Planten per meter</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min={0}
                          value={newVariant.planten_per_meter}
                          onChange={(e) => setNewVariant({ ...newVariant, planten_per_meter: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Leverancier</Label>
                        <Select
                          value={newVariant.supplier}
                          onValueChange={(value: 'Kwekerij' | 'Overig') => setNewVariant({ ...newVariant, supplier: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Kwekerij">Kwekerij</SelectItem>
                            <SelectItem value="Overig">Overig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Transport methode</Label>
                        <Select
                          value={newVariant.transport_method}
                          onValueChange={(value: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none') => setNewVariant({ ...newVariant, transport_method: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kuubkist">Kuubkist</SelectItem>
                            <SelectItem value="halve_kuubkist">Halve Kuubkist</SelectItem>
                            <SelectItem value="pallet">Pallet</SelectItem>
                            <SelectItem value="tuinplanten">Tuinplanten (€9,95)</SelectItem>
                            <SelectItem value="none">Geen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAddVariant} className="bg-green-600 hover:bg-green-700 text-white">Toevoegen</Button>
                      <Button variant="outline" onClick={() => setShowAddVariantForm(false)}>
                        Annuleren
                      </Button>
                    </div>
                  </Card>
                )}

                <div className="space-y-4">
                  {variants.map((variant, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold">
                          {variant.size} - {variant.type}
                        </h4>
                        <div className="flex gap-2">
                          {variant.isEditing ? (
                            <>
                              <Button size="sm" onClick={() => handleSaveVariantEdit(index)}>
                                Opslaan
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCancelVariantEdit(index)}>
                                Annuleren
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleEditVariant(index)}>
                                <Edit2 className="w-3 h-3 mr-1" />
                                Bewerken
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleRemoveVariant(index)}>
                                <Trash2 className="w-3 h-3 mr-1" />
                                Verwijderen
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {variant.isEditing ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Grootte</Label>
                            <Input
                              value={variant.size}
                              onChange={(e) => handleUpdateVariant(index, 'size', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Type</Label>
                            <Select
                              value={variant.type}
                              onValueChange={(value) => handleUpdateVariant(index, 'type', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Kluit">Kluit</SelectItem>
                                <SelectItem value="Pot">Pot</SelectItem>
                                <SelectItem value="Kale wortel">Kale wortel</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <VariantPricingCalculator
                              productId={selectedProduct?.id || null}
                              btwPercentage={formData.btw_percentage || 9}
                              variant={variant}
                              variantIndex={index}
                              onUpdatePrices={(idx, prices) => {
                                const updated = [...variants];
                                updated[idx] = { ...updated[idx], ...prices };
                                setVariants(updated);
                              }}
                              onUpdatePricingFields={(idx, fields) => {
                                const updated = [...variants];
                                updated[idx] = { ...updated[idx], ...fields };
                                setVariants(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>Voorraad</Label>
                            <Input
                              type="number"
                              min={0}
                              value={variant.stock_quantity}
                              onChange={(e) => handleUpdateVariant(index, 'stock_quantity', e.target.value === '' ? 0 : parseInt(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label>SKU</Label>
                            <Input
                              value={variant.sku}
                              onChange={(e) => handleUpdateVariant(index, 'sku', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Slug</Label>
                            <Input
                              value={variant.slug}
                              onChange={(e) => handleUpdateVariant(index, 'slug', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Minimale bestelhoeveelheid</Label>
                            <Input
                              type="number"
                              step={50}
                              min={0}
                              value={variant.minimum_order_quantity}
                              onChange={(e) => handleUpdateVariant(index, 'minimum_order_quantity', e.target.value === '' ? 0 : parseInt(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label>Planten per meter</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min={0}
                              value={variant.planten_per_meter}
                              onChange={(e) => handleUpdateVariant(index, 'planten_per_meter', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label>Leverancier</Label>
                            <Select
                              value={variant.supplier}
                              onValueChange={(value: 'Kwekerij' | 'Overig') => handleUpdateVariant(index, 'supplier', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Kwekerij">Kwekerij</SelectItem>
                                <SelectItem value="Overig">Overig</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Transport methode</Label>
                            <Select
                              value={variant.transport_method}
                              onValueChange={(value: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none') => handleUpdateVariant(index, 'transport_method', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="kuubkist">Kuubkist</SelectItem>
                                <SelectItem value="halve_kuubkist">Halve Kuubkist</SelectItem>
                                <SelectItem value="pallet">Pallet</SelectItem>
                                <SelectItem value="tuinplanten">Tuinplanten (€9,95)</SelectItem>
                                <SelectItem value="none">Geen</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {categories.find(c => c.id === formData.category_id)?.slug !== 'bomen' && (
                            <div className="col-span-2">
                              <Label className="flex items-center gap-2">
                                <CalendarClock className="w-4 h-4" />
                                Leverbaar vanaf
                              </Label>
                              <div className="flex items-center gap-3 mt-2">
                                <Switch
                                  checked={!!variant.available_from}
                                  onCheckedChange={(checked) => handleUpdateVariant(index, 'available_from', checked ? new Date().toISOString().split('T')[0] : null)}
                                />
                                <Input
                                  type="date"
                                  value={variant.available_from ? variant.available_from.split('T')[0] : ''}
                                  onChange={(e) => handleUpdateVariant(index, 'available_from', e.target.value || null)}
                                  disabled={!variant.available_from}
                                  className="w-48"
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Product blijft zichtbaar maar kan pas besteld worden vanaf deze datum.</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Prijzen per hoeveelheid</h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="p-3 bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className="bg-[#e7e1d6] text-[#1e2b22]" variant="secondary">25-50</Badge>
                                </div>
                                <p className="font-medium">€{variant.price_tier_1?.toFixed(2)} <span className="text-xs text-gray-600">excl.</span></p>
                                <p className="text-xs text-gray-600">€{calculatePriceInclBTW(variant.price_tier_1 || 0, formData.btw_percentage).toFixed(2)} incl.</p>
                              </div>
                              {variant.price_tier_2 && (
                                <div className="p-3 bg-green-50 rounded-lg">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-green-100 text-green-800" variant="secondary">51-75</Badge>
                                  </div>
                                  <p className="font-medium">€{variant.price_tier_2.toFixed(2)} <span className="text-xs text-gray-600">excl.</span></p>
                                  <p className="text-xs text-gray-600">€{calculatePriceInclBTW(variant.price_tier_2, formData.btw_percentage).toFixed(2)} incl.</p>
                                </div>
                              )}
                              {variant.price_tier_3 && (
                                <div className="p-3 bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-[#e7e1d6] text-[#1e2b22]" variant="secondary">76-100</Badge>
                                  </div>
                                  <p className="font-medium">€{variant.price_tier_3.toFixed(2)} <span className="text-xs text-gray-600">excl.</span></p>
                                  <p className="text-xs text-gray-600">€{calculatePriceInclBTW(variant.price_tier_3, formData.btw_percentage).toFixed(2)} incl.</p>
                                </div>
                              )}
                              {variant.price_tier_4 && (
                                <div className="p-3 bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-[#e7e1d6] text-[#1e2b22]" variant="secondary">101+</Badge>
                                  </div>
                                  <p className="font-medium">€{variant.price_tier_4.toFixed(2)} <span className="text-xs text-gray-600">excl.</span></p>
                                  <p className="text-xs text-gray-600">€{calculatePriceInclBTW(variant.price_tier_4, formData.btw_percentage).toFixed(2)} incl.</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Voorraad:</span>
                              <p className="font-medium">{variant.stock_quantity}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">SKU:</span>
                              <p className="font-medium">{variant.sku}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Slug:</span>
                              <p className="font-medium text-xs">{variant.slug}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Min. bestelhoeveelheid:</span>
                              <p className="font-medium">{variant.minimum_order_quantity}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Planten per meter:</span>
                              <p className="font-medium">{variant.planten_per_meter}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Leverancier:</span>
                              <p className="font-medium">{variant.supplier}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Transport methode:</span>
                              <p className="font-medium">
                                {variant.transport_method === 'kuubkist' ? 'Kuubkist' :
                                 variant.transport_method === 'halve_kuubkist' ? 'Halve Kuubkist' :
                                 variant.transport_method === 'pallet' ? 'Pallet' :
                                 variant.transport_method === 'tuinplanten' ? 'Tuinplanten' : 'Geen'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-4">
                        <Label>Variant afbeeldingen</Label>
                        <div className="mt-2 grid grid-cols-4 gap-4">
                          {variant.images?.map((image, imgIndex) => (
                            <div key={imgIndex} className="relative group">
                              <img
                                src={image.url}
                                alt={image.alt_text}
                                className={`w-full h-24 object-cover rounded-lg ${image.is_primary ? 'ring-2 ring-[#1e2b22]' : ''}`}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent && !parent.querySelector('.fallback-icon')) {
                                    const fallback = document.createElement('div');
                                    fallback.className = 'fallback-icon w-full h-24 rounded-lg bg-gray-100 flex items-center justify-center';
                                    fallback.innerHTML = '<svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>';
                                    parent.appendChild(fallback);
                                  }
                                }}
                              />
                              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {imgIndex > 0 && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleMoveVariantImage(index, imgIndex, 'up')}
                                    className="h-5 w-5 p-0"
                                  >
                                    <MoveUp className="w-3 h-3" />
                                  </Button>
                                )}
                                {imgIndex < (variant.images?.length || 0) - 1 && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleMoveVariantImage(index, imgIndex, 'down')}
                                    className="h-5 w-5 p-0"
                                  >
                                    <MoveDown className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveVariantImage(index, imgIndex)}
                                  className="h-5 w-5 p-0"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                              {!image.is_primary && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleSetPrimaryVariantImage(index, imgIndex)}
                                  className="absolute bottom-1 left-1 h-5 text-xs px-1"
                                >
                                  Primair
                                </Button>
                              )}
                              {image.is_primary && (
                                <Badge className="absolute bottom-1 left-1 bg-[#1e2b22] text-white h-5 text-xs">
                                  Primair
                                </Badge>
                              )}
                            </div>
                          ))}
                          <label className="w-full h-24 border-2 border-dashed border-[#cfcac2] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#1e2b22] transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => handleVariantImageUpload(e, index)}
                              disabled={uploadingImage}
                            />
                            <Upload className="w-6 h-6 text-gray-400" />
                          </label>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {variants.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nog geen varianten toegevoegd</p>
                    <p className="text-sm">Klik op "Variant toevoegen" om te beginnen</p>
                  </div>
                )}


              </TabsContent>
            )}

            <TabsContent value="faqs" className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">FAQs beheren</h3>
              </div>

              <Card className="p-4 bg-gray-50">
                <h4 className="font-semibold mb-4">Nieuwe FAQ</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Vraag</Label>
                    <Input
                      value={newFAQ.question}
                      onChange={(e) => setNewFAQ({ ...newFAQ, question: e.target.value })}
                      placeholder="Bijv. Hoeveel water heeft deze plant nodig?"
                    />
                  </div>
                  <div>
                    <Label>Antwoord</Label>
                    <Textarea
                      value={newFAQ.answer}
                      onChange={(e) => setNewFAQ({ ...newFAQ, answer: e.target.value })}
                      rows={4}
                      placeholder="Geef hier het antwoord..."
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="new-faq-active"
                      checked={newFAQ.active}
                      onCheckedChange={(checked) => setNewFAQ({ ...newFAQ, active: checked })}
                    />
                    <Label htmlFor="new-faq-active">Actief</Label>
                  </div>
                  <Button onClick={handleAddFAQ}>
                    <Plus className="w-4 h-4 mr-2" />
                    FAQ toevoegen
                  </Button>
                </div>
              </Card>

              <div className="space-y-4">
                {productFAQs.map((faq, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold">FAQ {index + 1}</h4>
                      <div className="flex gap-2">
                        {index > 0 && (
                          <Button size="sm" variant="outline" onClick={() => handleMoveFAQ(index, 'up')}>
                            <MoveUp className="w-3 h-3" />
                          </Button>
                        )}
                        {index < productFAQs.length - 1 && (
                          <Button size="sm" variant="outline" onClick={() => handleMoveFAQ(index, 'down')}>
                            <MoveDown className="w-3 h-3" />
                          </Button>
                        )}
                        {faq.isEditing ? (
                          <>
                            <Button size="sm" onClick={() => handleSaveFAQEdit(index)}>
                              Opslaan
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleCancelFAQEdit(index)}>
                              Annuleren
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleEditFAQ(index)}>
                              <Edit2 className="w-3 h-3 mr-1" />
                              Bewerken
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleRemoveFAQ(index)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Verwijderen
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {faq.isEditing ? (
                      <div className="space-y-4">
                        <div>
                          <Label>Vraag</Label>
                          <Input
                            value={faq.question}
                            onChange={(e) => handleUpdateFAQ(index, 'question', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Antwoord</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => handleUpdateFAQ(index, 'answer', e.target.value)}
                            rows={4}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`faq-active-${index}`}
                            checked={faq.active}
                            onCheckedChange={(checked) => handleUpdateFAQ(index, 'active', checked)}
                          />
                          <Label htmlFor={`faq-active-${index}`}>Actief</Label>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div>
                          <span className="text-gray-600 text-sm">Vraag:</span>
                          <p className="font-medium">{faq.question}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 text-sm">Antwoord:</span>
                          <p className="text-sm">{faq.answer}</p>
                        </div>
                        <div>
                          <Badge variant={faq.active ? 'default' : 'secondary'}>
                            {faq.active ? 'Actief' : 'Inactief'}
                          </Badge>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {productFAQs.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <HelpCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nog geen FAQs toegevoegd</p>
                  <p className="text-sm">Vul het formulier hierboven in om een FAQ toe te voegen</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 mt-6 -mx-6 px-6 pb-2">
            {message && message.type === 'error' && (
              <div className="w-full mb-3 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{message.text}</span>
              </div>
            )}
            <div className="flex gap-2 justify-end w-full">
              <Button variant="outline" onClick={handleCloseDialog}>
                Annuleren
              </Button>
              {selectedProduct && (
                <Button
                  variant="outline"
                  onClick={() => handleSaveProduct(false)}
                  className="border-[#1e2b22] text-[#1e2b22] hover:bg-[#1e2b22] hover:text-white"
                >
                  Bijwerken
                </Button>
              )}
              <Button
                onClick={() => handleSaveProduct(true)}
                className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
              >
                {selectedProduct ? 'Bijwerken en sluiten' : 'Toevoegen'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Product verwijderen</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je "{productToDelete?.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={handleDeleteProduct}>
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
